<template>
  <div class="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
    <!-- Luxury Background Pattern -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-full blur-xl opacity-10"></div>
      <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-orange-400 to-red-600 rounded-full blur-xl opacity-10"></div>
      <div class="absolute bottom-40 left-1/4 w-28 h-28 bg-gradient-to-br from-yellow-300 to-orange-500 rounded-full blur-xl opacity-10"></div>
      <div class="absolute bottom-20 right-1/3 w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full blur-xl opacity-10"></div>
    </div>

    <!-- Header with Logo -->
    <div class="relative z-10 bg-white/80 backdrop-blur-sm shadow-lg border-b border-amber-200/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="w-12 h-12 mr-4">
              <img 
                src="/logo6.png" 
                alt="Annzz Logo" 
                class="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900 font-luxury">Annzz</h1>
              <p class="text-sm text-gray-600 font-light">Exclusive Product View</p>
            </div>
          </div>
          
          <router-link 
            to="/landing" 
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-800 transition-colors duration-200"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </router-link>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="relative z-10 flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p class="text-gray-600 font-light">Loading product details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="relative z-10 flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2 font-luxury">Product Not Found</h2>
        <p class="text-gray-600 font-light mb-6">{{ error }}</p>
        <router-link 
          to="/landing" 
          class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          Return to Home
        </router-link>
      </div>
    </div>

    <!-- Product Details -->
    <div v-else-if="product" class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-amber-200/50 overflow-hidden">
        <!-- Product Header -->
        <div class="p-8 border-b border-amber-200/50">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h1 class="text-4xl font-bold text-gray-900 mb-4 font-luxury">{{ product.name }}</h1>
              <p class="text-xl text-gray-600 font-light mb-2">SKU: {{ product.sku }}</p>
              <p v-if="accessedViaSecret" class="text-sm text-amber-600 font-medium">
                🔐 Accessed via Secret Key
              </p>
            </div>
            <div class="ml-8">
              <span class="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                {{ product.category }}
              </span>
            </div>
          </div>
        </div>

        <!-- Product Content -->
        <div class="p-8">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Product Image -->
            <div class="space-y-4">
              <div 
                @click="openImageModal"
                class="aspect-w-1 aspect-h-1 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <div v-if="productImages.length > 0" class="w-full h-96 bg-gradient-to-br from-amber-100 to-yellow-200 relative group">
                  <img 
                    :src="getImageUrl(productImages[0])" 
                    :alt="product.name"
                    class="w-full h-full object-contain"
                    @error="handleImageError"
                  />
                  <!-- View Images Overlay -->
                  <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div class="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium text-gray-900">
                      <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Images
                    </div>
                  </div>
                </div>
                <div v-else class="w-full h-96 bg-gradient-to-br from-amber-100 to-yellow-200 flex items-center justify-center">
                  <span class="text-amber-600 text-6xl font-bold">{{ product.name.charAt(0) }}</span>
                </div>
              </div>
              <p v-if="productImages.length > 0" class="text-center text-sm text-gray-600 font-light">Click to view product images</p>
            </div>

            <!-- Product Info -->
            <div class="space-y-6">
              <!-- Product Specifications - Hidden for secret access -->
              <div v-if="!accessedViaSecret" class="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200/50">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 font-luxury">Product Specifications</h3>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-gray-600 font-light">Category:</span>
                    <span class="font-medium">{{ product.category }}</span>
                  </div>
                  <div v-if="product.metal" class="flex justify-between">
                    <span class="text-gray-600 font-light">Metal:</span>
                    <span class="font-medium">{{ product.metal }}</span>
                  </div>
                  <div v-if="product.gross_weight" class="flex justify-between">
                    <span class="text-gray-600 font-light">Gross Weight:</span>
                    <span class="font-medium">{{ product.gross_weight }}g</span>
                  </div>
                  <div v-if="product.stone_weight" class="flex justify-between">
                    <span class="text-gray-600 font-light">Stone Weight:</span>
                    <span class="font-medium">{{ product.stone_weight }}g</span>
                  </div>
                  <div v-if="product.net_weight" class="flex justify-between">
                    <span class="text-gray-600 font-light">Net Weight:</span>
                    <span class="font-medium">{{ product.net_weight }}g</span>
                  </div>
                  <!-- Business fields hidden from customers -->
                </div>
              </div>

              <!-- Labels/Tags Section -->
              <div v-if="product.labels && product.labels.length > 0" class="bg-white rounded-xl p-6 border border-amber-200/50">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 font-luxury">Labels & Tags</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="label in product.labels" :key="label.id" :class="[
                    'px-3 py-2 rounded-full text-sm font-semibold',
                    label.type === 'category' ? 'bg-orange-100 text-orange-700 border border-orange-300' :
                    label.type === 'subcategory' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                    'bg-amber-100 text-amber-700 border border-amber-300'
                  ]">
                    {{ label.name }}
                  </span>
                </div>
              </div>

              <div v-if="product.description" class="bg-white rounded-xl p-6 border border-amber-200/50">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 font-luxury">Description</h3>
                <p class="text-gray-600 font-light leading-relaxed">{{ product.description }}</p>
              </div>

              <!-- Action Buttons -->
              <div class="space-y-4">
                <button
                  v-if="canUseCart"
                  @click="addToCart"
                  :disabled="addingToCart"
                  class="w-full bg-gradient-to-r from-amber-600 to-yellow-600 text-white py-4 rounded-xl hover:from-amber-700 hover:to-yellow-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="addingToCart">Adding to Cart...</span>
                  <span v-else>Add to Cart</span>
                </button>
                
                <router-link 
                  to="/catalogue" 
                  class="block w-full text-center bg-white border border-amber-200 text-amber-600 py-4 rounded-xl hover:bg-amber-50 transition-all duration-200 font-medium"
                >
                  View All Products
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Viewer Modal -->
    <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeImageModal"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl border border-amber-200/20 max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b border-amber-200/50">
          <div class="flex items-center">
            <div class="w-10 h-10 mr-3">
              <img 
                src="/logo6.png" 
                alt="Annzz Logo" 
                class="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 class="text-xl font-bold text-gray-900 font-luxury">{{ product?.name }}</h3>
              <p class="text-sm text-gray-600 font-light">Product Images</p>
            </div>
          </div>
          <button 
            @click="closeImageModal"
            class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Image Content -->
        <div class="p-6">
          <div v-if="productImages.length > 0" class="space-y-6">
            <!-- Main Image Display -->
            <div class="relative">
              <div class="w-full max-w-3xl mx-auto aspect-w-16 aspect-h-9 bg-black rounded-xl overflow-hidden flex items-center justify-center" style="min-height:360px; max-height:480px;">
                <img 
                  :src="getImageUrl(currentImage)" 
                  :alt="`${product?.name} - Image ${currentImageIndex + 1}`"
                  class="w-full h-full object-contain object-center"
                  style="min-height:360px; max-height:480px;"
                  @error="handleImageError"
                />
              </div>
              
              <!-- Navigation Arrows -->
              <button 
                v-if="productImages.length > 1"
                @click="previousImage"
                class="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
              >
                <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                v-if="productImages.length > 1"
                @click="nextImage"
                class="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
              >
                <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <!-- Image Counter -->
              <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm font-medium">
                {{ currentImageIndex + 1 }} / {{ productImages.length }}
              </div>
            </div>

            <!-- Thumbnail Navigation -->
            <div v-if="productImages.length > 1" class="flex space-x-2 overflow-x-auto pb-2">
              <button
                v-for="(image, index) in productImages"
                :key="index"
                @click="setCurrentImage(index)"
                :class="[
                  'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200',
                  currentImageIndex === index 
                    ? 'border-amber-500 shadow-lg' 
                    : 'border-gray-200 hover:border-amber-300'
                ]"
              >
                <img 
                  :src="getImageUrl(image)" 
                  :alt="`Thumbnail ${index + 1}`"
                  class="w-full h-full object-contain"
                  @error="handleImageError"
                />
              </button>
            </div>
          </div>

          <!-- No Images State -->
          <div v-else class="text-center py-12">
            <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Images Available</h3>
            <p class="text-gray-600">This product doesn't have any images yet.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { productsAPI } from '@/services/api'
import { useUserStore } from '@/stores/user'
import { useNotificationStore } from '@/stores/notification'
import { cartAPI } from '@/services/api' // Added import for cartAPI

export default {
  name: 'ProductDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()
    const notificationStore = useNotificationStore()
    
    const product = ref(null)
    const loading = ref(true)
    const error = ref(null)
    const addingToCart = ref(false)

    // Only customers and admins have a cart; guests see the button so it can prompt login
    const canUseCart = computed(() =>
      !userStore.isAuthenticated || ['customer', 'admin'].includes(userStore.user?.role)
    )
    
    // Image viewer state
    const showImageModal = ref(false)
    const currentImageIndex = ref(0)
    
    // Determine if accessed via secret key
    const accessedViaSecret = computed(() => {
      return route.name === 'SecretProductDetail'
    })

    const productImages = computed(() => {
      if (!product.value?.images) return []
      try {
        return Array.isArray(product.value.images) 
          ? product.value.images 
          : JSON.parse(product.value.images)
      } catch {
        return []
      }
    })

    const currentImage = computed(() => {
      if (productImages.value.length === 0) return ''
      return productImages.value[currentImageIndex.value] || productImages.value[0]
    })
    
    const loadProduct = async () => {
      try {
        loading.value = true
        error.value = null
        
        console.log('ProductDetail: Starting to load product')
        console.log('ProductDetail: Route params:', route.params)
        console.log('ProductDetail: Route name:', route.name)
        
        let response
        if (accessedViaSecret.value) {
          console.log('ProductDetail: Loading via secret key:', route.params.secretKey)
          // Access via secret key without authentication
          response = await productsAPI.getProductBySecretKeyPublic(route.params.secretKey)
        } else {
          console.log('ProductDetail: Loading via SKU:', route.params.sku)
          // Access via SKU
          response = await productsAPI.getProduct(route.params.sku, true)
        }
        
        console.log('ProductDetail: API response received:', response.data)
        product.value = response.data.product
        console.log('ProductDetail: Product loaded successfully:', product.value)
        
      } catch (err) {
        console.error('ProductDetail: Error loading product:', err)
        if (err.response?.status === 404) {
          error.value = 'Product not found or secret key invalid'
        } else {
          error.value = 'Failed to load product details'
        }
      } finally {
        loading.value = false
      }
    }
    
    const addToCart = async () => {
      if (!userStore.isAuthenticated) {
        // Store the current product info in session storage for after OTP validation
        sessionStorage.setItem('pendingCartProduct', JSON.stringify({
          productId: product.value.id,
          secretKey: route.params.secretKey || null,
          accessedViaSecret: accessedViaSecret.value
        }))
        
        // Redirect to landing page for OTP validation
        router.push('/')
        return
      }
      
      try {
        addingToCart.value = true
        // Actually add to cart using the API
        await cartAPI.addToCart(product.value, 1)
        
        // Show success message
        notificationStore.showSuccess('Added to Cart', 'Product has been added to your cart successfully!')
        
      } catch (err) {
        console.error('Error adding to cart:', err)
        notificationStore.showError('Error', 'Failed to add product to cart')
      } finally {
        addingToCart.value = false
      }
    }

    // Image Viewer Methods
    const openImageModal = () => {
      if (productImages.value.length > 0) {
        currentImageIndex.value = 0
        showImageModal.value = true
      }
    }

    const closeImageModal = () => {
      showImageModal.value = false
      currentImageIndex.value = 0
    }

    const nextImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = (currentImageIndex.value + 1) % productImages.value.length
      }
    }

    const previousImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = currentImageIndex.value === 0 
          ? productImages.value.length - 1 
          : currentImageIndex.value - 1
      }
    }

    const setCurrentImage = (index) => {
      currentImageIndex.value = index
    }

    const getImageUrl = (imageName) => {
      // Use a working placeholder service or return a fallback
      if (!imageName) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xOTIgMTI4SDE2MFY5NkgxOTJWMTA0SDE2OFYxMjhIMTkyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjA4IDEyOEgyMjRWMTA0SDIwOFY5NkgyMzJWMTA0SDIyNFYxMjhIMjA4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjE2IDEyOEgyMzJWMTA0SDIxNlY5NkgyNDBWMTA0SDIzMlYxMjhIMjE2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjI0IDEyOEgyNDBWMTA0SDIyNFY5NkgyNDhWMTA0SDI0MFYxMjhIMjI0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjMyIDEyOEgyNDhWMTA0SDIzMlY5NkgyNTZWMTA0SDI0OFYxMjhIMjMyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjQwIDEyOEgyNTZWMTA0SDI0MFY5NkgyNjRWMTA0SDI1NlYxMjhIMjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjQ4IDEyOEgyNjRWMTA0SDI0OFY5NkgyNzJWMTA0SDI2NFYxMjhIMjQ4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjU2IDEyOEgyNzJWMTA0SDM1NlY5NkgyODBWMTA0SDI3MlYxMjhIMjU2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjY0IDEyOEgyODBWMTA0SDI2NFY5NkgyODhWMTA0SDI4MFYxMjhIMjY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjcyIDEyOEgyODhWMTA0SDI3MlY5NkgyOTZWMTA0SDI4OFYxMjhIMzcyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzgwIDEyOEgyOTJWMTA0SDM4MFY5NkgyMDRWMTA0SDI5NlYxMjhIMzgwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzg4IDEyOEgyMDRWMTA0SDM4OFY5NkgyMTJWMTA0SDIwNFYxMjhIMzg4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzk2IDEyOEgyMTJWMTA0SDM5NlY5NkgyMjBWMTA0SDIxMlYxMjhIMzk2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNNDA0IDEyOEgyMjBWMTA0SDQwNFY5NkgyMjhWMTA0SDIyMFYxMjhINDA0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const handleImageError = (event) => {
      // Replace broken image with placeholder
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkZGRkZGIi8+CjxwYXRoIGQ9Ik0xMDAgNTBDMTIzLjE5NiA1MCAxNDIgNjguODA0IDE0MiA5MkMxNDIgMTE1LjE5NiAxMjMuMTk2IDEzNCAxMDAgMTM0Qzc2LjgwNCAxMzQgNTggMTE1LjE5NiA1OCA5MkM1OCA2OC44MDQgNzYuODA0IDUwIDEwMCA1MFoiIGZpbGw9IiNGRkMxMDciLz4KPHBhdGggZD0iTTEwMCAxNTRDMTIzLjE5NiAxNTQgMTQyIDE3Mi44MDQgMTQyIDE5NkMxNDIgMjE5LjE5NiAxMjMuMTk2IDIzOCAxMDAgMjM4Qzc2LjgwNCAyMzggNTggMjE5LjE5NiA1OCAxOTZDNTggMTcyLjgwNCA3Ni44MDQgMTU0IDEwMCAxNTRaIiBmaWxsPSIjRkZDMTA3Ii8+Cjwvc3ZnPgo='
    }
    
    onMounted(() => {
      loadProduct()
    })
    
    return {
      product,
      loading,
      error,
      addingToCart,
      accessedViaSecret,
      addToCart,
      canUseCart,
      showImageModal,
      currentImageIndex,
      productImages,
      currentImage,
      openImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      setCurrentImage,
      getImageUrl,
      handleImageError
    }
  }
}
</script> 